import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/types';
import { useAppColors } from '../../hooks/useAppColors';
import { useForgotPassword } from '../../hooks/useAuth';
import AuthLayout from './AuthLayout';

type Props = NativeStackScreenProps<AuthStackParamList, 'ForgotPassword'>;

export default function ForgotPasswordScreen({ navigation }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const forgotPassword = useForgotPassword();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    setError('');
    if (!email.trim()) {
      setError(t('auth.emailRequired'));
      return;
    }
    forgotPassword.mutate(email.trim(), {
      onSuccess: () => setSent(true),
      onError: (err) => setError(err.message),
    });
  };

  return (
    <AuthLayout
      title={t('auth.forgotPassword')}
      subtitle={t('auth.forgotPasswordSubtitle')}
    >
        {sent ? (
          <>
            <Text style={[styles.successText, { color: colors.accent }]}>{t('auth.resetEmailSent')}</Text>
            <Text style={[styles.successDetail, { color: colors.textSecondary }]}>{t('auth.resetEmailSentDetail')}</Text>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.accent }]}
              onPress={() => navigation.navigate('Login')}
            >
              <Text style={[styles.buttonText, { color: colors.accentText }]}>{t('auth.backToSignIn')}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TextInput
              label={t('auth.email')}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              mode="outlined"
              style={styles.input}
            />

            {!!error && <Text style={[styles.error, { color: colors.error }]}>{error}</Text>}

            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.accent }]}
              onPress={handleSend}
              disabled={forgotPassword.isPending}
            >
              <Text style={[styles.buttonText, { color: colors.accentText }]}>
                {forgotPassword.isPending ? t('common.loading') : t('auth.sendResetLink')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.linkButton} onPress={() => navigation.goBack()}>
              <Text style={[styles.linkText, { color: colors.accent }]}>{t('common.back')}</Text>
            </TouchableOpacity>
        </>
        )}
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  successText: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  successDetail: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
  },
});
